import {computed, type MaybeRefOrGetter, toValue} from "vue";
import {storeToRefs} from "pinia";
import Translator from "@/utils/common/Translator.ts";
import {useCrossFilterStore} from "@/stores/crossFilterStore.ts";
import CrossFilterRenderer from "@/components/bi/CrossFilterRenderer.ts";

/**
 * Filter-badge state shared by every dashboard widget (EChart, Table, EnhancedTable,
 * MetricsCards). Exposes whether the widget is currently emitting cross-filters or is being
 * filtered by others, plus the tooltip text rendered on the badge. Centralizing this keeps
 * the badge behavior identical across widget kinds and removes three copies of
 * `buildFilterTooltip` that drifted apart over time.
 */
export function useBIFilterBadges(
  widgetId: MaybeRefOrGetter<string | undefined>,
  appliedCrossFilterCodes: MaybeRefOrGetter<string[] | undefined>,
) {
  const crossFilterStore = useCrossFilterStore();
  const {filledQuestions} = storeToRefs(crossFilterStore);

  const emittedFilters = computed(() => crossFilterStore.getFiltersBySource(toValue(widgetId)));
  const isFilterSource = computed(() => emittedFilters.value.length > 0);
  const appliedCodes = computed<string[]>(() => toValue(appliedCrossFilterCodes) ?? []);
  const isFilteredByOther = computed(() => appliedCodes.value.length > 0);

  function buildTooltip(headerKey: string, codes: string[]): string {
    const lines = [Translator.translate(headerKey) + ":"];
    for (const code of codes) {
      const filled = filledQuestions.value.get(code);
      if (!filled) continue;
      const questionField = crossFilterStore.findQuestionField(code);
      const label = CrossFilterRenderer.renderLabel(questionField) || code;
      const value = CrossFilterRenderer.renderValue(filled, questionField);
      lines.push(`• ${label} (${code}): ${value}`);
    }
    return lines.join("\n");
  }

  const filteredByTooltip = computed<string>(() => {
    if (!isFilteredByOther.value) return "";
    return buildTooltip("biFilteredBy", appliedCodes.value);
  });

  const emittingTooltip = computed<string>(() => {
    if (!isFilterSource.value) return "";
    const codes = emittedFilters.value.map(f => f.id).filter((id): id is string => !!id);
    return buildTooltip("biEmitting", codes);
  });

  return {
    isFilterSource,
    isFilteredByOther,
    filteredByTooltip,
    emittingTooltip,
  };
}
